/**
 * Get an array of an HTML elements classes
 * @param {HTMLElement} element
 * @returns {string[]} string array of class names
 */
const getClasses = (element) => {
  // Return without error if element or class are missing
  if (!element) return []
  return element.className.split(' ').filter(Boolean)
}

/**
 * Does a class exist on an element
 * @param {HTMLElement} element
 * @returns {Boolean} true if element has class
 */
const hasClass = (element, className) => {
  // Return without error if element or class are missing
  if (!element || !className) return false
  return getClasses(element).includes(className)
}

/**
 * Remove a class on a HTML element
 * @param {HTMLElement} element
 * @param {string} className
 */
const removeClass = (element, className) => {
  // Return without error if element or class are missing
  if (!element || !className) return
  if (hasClass(element, className)) {
    // Array of all existing classes
    const existingClasses = getClasses(element)
    // String of existing classes minus the class to remove
    const newClasses = existingClasses
      .filter((existingClass) => existingClass !== className)
      .join(' ')
    // Set class attribute to the new classes
    element.setAttribute('class', newClasses)
  }
}

/**
 * Add a class on a HTML element
 * @param {HTMLElement} element
 * @param {string} className
 */
const addClass = (element, className) => {
  // Return without error if element or class are missing
  if (!element || !className) return
  if (!hasClass(element, className)) {
    // Set class attribute to the new classes
    element.setAttribute('class', `${element.className} ${className}`.trim())
  }
}

/**
 * Toggle a class on a HTML element
 * @param {HTMLElement} element
 * @param {string} className
 */
const toggleClass = (element, className) => {
  if (!element || !className) return
  if (hasClass(element, className)) {
    removeClass(element, className)
  } else {
    addClass(element, className)
  }
}

/**
 * Toggle a boolean attribute on a HTML element
 * @param {HTMLElement} element
 * @param {string} attr
 */
const toggleAttribute = (element, attr) => {
  // Return without error if element or attr are missing
  if (!element || !attr) return
  // Toggle attribute value. Treat no existing attr same as when set to false
  const value = element.getAttribute(attr) === 'true' ? 'false' : 'true'
  element.setAttribute(attr, value)
}

/**
 * Toggle a toggle a class on conditional content for an input based on checked state
 * @param {HTMLElement} input input element
 * @param {string} className class to toggle
 */
const toggleConditionalInput = (input, className) => {
  // Return without error if input or class are missing
  if (!input || !className) return
  // If the input has conditional content it had a data-aria-controls attribute
  const conditionalId = input.getAttribute('aria-controls')
  if (conditionalId) {
    // Get the conditional element from the input data-aria-controls attribute
    const conditionalElement = document.getElementById(conditionalId)
    if (conditionalElement) {
      if (input.checked) {
        conditionalElement.classList.remove(className)
      } else {
        conditionalElement.classList.add(className)
      }
    }
  }
}

/**
 * Move focus to element
 *
 * Sets tabindex to -1 to make the element programmatically focusable,
 * but removes it on blur as the element doesn't need to be focused again.
 *
 * Original code taken from GDS (Government Digital Service)
 * {@link https://github.com/alphagov/govuk-frontend}
 *
 * @template {HTMLElement} FocusElement
 * @param {FocusElement} $element - HTML element
 * @param {object} [options] - Handler options
 * @param {function(this: FocusElement): void} [options.onBeforeFocus] - Callback before focus
 * @param {function(this: FocusElement): void} [options.onBlur] - Callback on blur
 */
function setFocus($element, options = {}) {
  const isFocusable = $element.getAttribute('tabindex')

  if (!isFocusable) {
    $element.setAttribute('tabindex', '-1')
  }

  /**
   * Handle element focus
   */
  function onFocus() {
    $element.removeEventListener('focus', onFocus)
    $element.addEventListener('blur', onBlur)
  }

  /**
   * Handle element blur
   */
  function onBlur() {
    $element.removeEventListener('blur', onBlur)

    if (options.onBlur) {
      options.onBlur.call($element)
    }

    if (!isFocusable) {
      $element.removeAttribute('tabindex')
    }
  }

  // Add listener to reset element on blur, after focus
  $element.addEventListener('focus', onFocus)

  // Focus element
  if (options.onBeforeFocus) {
    options.onBeforeFocus.call($element)
  }

  $element.focus()
}

module.exports = {
  toggleClass,
  toggleAttribute,
  toggleConditionalInput,
  setFocus
}
